import Box from "@/common/components/Box";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import ResumeItem from "@/common/components/ResumeItem";
import { cn } from "@/common/utils/cn";
import { Building2Icon } from "lucide-react";
import { useMemo } from "react";
import { maintenanceForwardingsDirectiveEntryKlasses } from "../../../../../../../constants/maintenanceForwardingsDirectiveEntryKlasses.constant";
import { useForwardingTabContext } from "../../../../../../../hooks/forwardings/useForwardingTabContext";
import ManualEntryProposalsClientAccept from "../../ManualEntryProposals/ClientAccept";
import ManualEntryProposalsClientReject from "../../ManualEntryProposals/ClientReject";
import EstablishmentChoiceEntryProposal from "../../Proposal";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/features/forwardings/types/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenanceForwardingEstablishmentChoiceEntryKlass } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/features/forwardings/types/forwardings/establishmentChoiceEntries/establishmentChoiceEntry.type";

export interface MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionManualEntryProps {
  showProposalDiagnostic?: boolean;
}

const MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionManualEntry = ({
  showProposalDiagnostic,
}: MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionManualEntryProps) => {
  const { forwarding, directiveEntryKlass } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass,
    MaintenanceForwardingEstablishmentChoiceEntryKlass.ManualEntry
  >();

  const isProposalCanceled = useMemo(() => {
    return forwarding?.establishment_choice_entry?.actual_proposal?.status === 'canceled';
  }, [forwarding?.establishment_choice_entry?.actual_proposal?.status]);

  const isAwaitingClientResponse = useMemo(() => {
    return forwarding?.establishment_choice_entry?.actual_proposal?.status === 'awaiting_client_response';
  }, [forwarding?.establishment_choice_entry?.actual_proposal?.status]);

  return (
    <Box className={cn(
      'p-4 flex flex-col gap-4',
      isAwaitingClientResponse && 'bg-yellow-op-2',
    )}>
      <div className="flex items-center gap-2 justify-between">
        <div>
          <p className="text-lg font-semibold">
            Estabelecimento para {directiveEntryKlass && maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.label}
          </p>
        </div>

        {isAwaitingClientResponse && forwarding?.establishment_choice_entry?.actual_proposal && (
          <div className="flex items-center gap-3">
            <ManualEntryProposalsClientReject
              proposal={forwarding?.establishment_choice_entry?.actual_proposal}
            />

            <ManualEntryProposalsClientAccept
              proposal={forwarding?.establishment_choice_entry?.actual_proposal}
            />
          </div>
        )}

        {isProposalCanceled && (
          <p className="text-danger-soft">
            Proposta declinada
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        {forwarding?.establishment_choice_entry?.establishment && (
          <ResumeItem
            label="Estabelecimento escolhido"
            value={(
              <ImageWithDisplayName
                placeholderIcon={Building2Icon}
                imageUrl={forwarding?.establishment_choice_entry?.establishment?.general_information?.company_logo?.url}
                displayName={forwarding?.establishment_choice_entry?.establishment?.general_information?.fantasy_name || '--'}
                containerClassName="gap-1.5"
                imageClassName="size-5"
                iconClassName="size-3.5"
                displayNameClassName="text-xs"
              />
            )}
            className="text-xs"
            containerClassName="gap-1"
            valueClassName="font-bold text-sm"
          />
        )}
      </div>

      {forwarding?.establishment_choice_entry?.actual_proposal && (
        <EstablishmentChoiceEntryProposal
          proposal={forwarding?.establishment_choice_entry?.actual_proposal}
          showProposalDiagnostic={showProposalDiagnostic && !isProposalCanceled}
        />
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionManualEntry;
